import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { installedPackKeysForNav } from '@/config/solutionPackNavGates';
import apiService from '@/services/api.service';

export type SolutionPacksResponse = {
  installedPackKeys: string[] | null;
};

async function fetchMySolutionPacks(): Promise<SolutionPacksResponse> {
  const response = await apiService.get<{
    installedPackKeys?: string[] | null;
    error?: string;
  }>('/auth/me/solution-packs');
  const data = response.data;
  if (!data || !('installedPackKeys' in data)) {
    return { installedPackKeys: null };
  }
  if (data.installedPackKeys === null) {
    return { installedPackKeys: null };
  }
  if (!Array.isArray(data.installedPackKeys)) {
    return { installedPackKeys: null };
  }
  return { installedPackKeys: data.installedPackKeys.map(String) };
}

/**
 * Installed ECS solution packs for the logged-in company (nav gating).
 * `installedPackKeys === null` stays fail-open for existing verticals.
 * Education treats that unknown state as hidden.
 */
export function useInstalledSolutionPacks() {
  const companyId = useAuthStore((s) => s.user?.company_id);
  const userId = useAuthStore((s) => s.user?.id);

  const query = useQuery({
    queryKey: ['solution-packs', companyId || '', userId || ''],
    queryFn: fetchMySolutionPacks,
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
    retry: 1,
  });

  const installedPackKeys = installedPackKeysForNav({
    isLoading: query.isLoading,
    isError: query.isError,
    installedPackKeys: query.data?.installedPackKeys,
  });

  return {
    installedPackKeys,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
